/**
 * -----------------------------------------------------------------------------
 * Managed Account Users Listing Page
 * -----------------------------------------------------------------------------
 */
import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";
import { useDispatch, useSelector } from "react-redux";
import {
  setListingQueryParams,
  setModalsData,
  TListingQueryParams,
  TModalsState,
} from "../../api/store/commonSlice";
import {
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../components/admin/formFields";
import HeaderView from "../../components/admin/headerView";
import AdminTable from "../../components/adminTable";
import ListingPageSubHeader from "../../components/listingPageSubHeader";
import ListingTableHeader from "../../components/listingTableHeader";
import {
  EditListingColumnsButton,
  ModalEditListingColumns,
  TListingColumn,
} from "../../components/modalEditListingColumns";
import Pagination, { TPaginationSelected } from "../../components/pagination";
import { TAdminTableRowData } from "../../components/types";
import { APP_CONFIG } from "../../constants/constants";
import { routeUrls } from "../../navigation/routeUrls";
import { useOrganizationUsersQuery } from "../../api/network/adminApiServices";
import { useSysadminLoginAsAccountUserMutation } from "../../api/network/sysadminApiServices";
import { useLoginHandler } from "../../hooks/loginHandler";

const all_columns = [
  "User ID",
  "Description",
  "Role",
  "Contact Name",
  "Email",
  "Action",
];

const ScreenManagedAccountUsers = () => {
  const { t: tMain } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { accountId } = useParams();

  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { users: orgUsersQueryParams } = listingQueryParams;

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );

  const [columns, setColumns] = useState<TListingColumn[]>(
    all_columns.map((colItem) => ({ name: colItem, show: true }))
  );

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        users: { ...orgUsersQueryParams, page: 1, search: value },
      })
    );
  }, 500);

  const { data: usersData, isFetching } = useOrganizationUsersQuery(
    {
      ...orgUsersQueryParams,
      accountid: accountId,
    },
    {
      skip: !accountId,
    }
  );

  const results = usersData?.results || [];
  const count = usersData?.count || results.length || 0;

  const loginHandler = useLoginHandler({ screen: "loginScreen" });
  const [sysAdminLoginAsAccountUserApiTrigger] =
    useSysadminLoginAsAccountUserMutation();

  const handleLogin = (userid: string) => {
    console.log("accountId", accountId);
    console.log("userid", userid);
    dispatch(setModalsData({ ...modalsState, showPrimaryOverlay: true }));
    sysAdminLoginAsAccountUserApiTrigger({
      user_id: userid,
      account_id: accountId,
    })
      .unwrap()
      .then((response) => {
        loginHandler.prepareSwitchSysadminToAccountUser();
        loginHandler.loginSuccessHandler(response);
        window.location.reload();
      })
      .catch(loginHandler.loginErrorHandler)
      .finally(() => {
        dispatch(setModalsData({ ...modalsState, showPrimaryOverlay: false }));
      });
  };

  const onBackButtonClick = () => {
    navigate(routeUrls.dashboardChildren.managedAccounts);
  };

  const tableData: TAdminTableRowData[] = results.map((item: any) => ({
    navLink: "",
    cellData: [
      item?.userid || "-",
      item?.description || "-",
      item?.roleid || "-",
      item?.contactname || "-",
      item?.contactemail || "-",
      <AdminFormFieldSubmit
        label={tMain("login")}
        variant="primary"
        customWrapperClass="w-24"
        onClick={() => handleLogin(item.userid)}
        disabled={item?.isactive !== 1}
      />,
    ].filter((_, index) => columns[index].show),
    isActive: item?.isactive === 1,
  }));

  return (
    <>
      <HeaderView
        title={`${accountId}`}
        showBackButton={true}
        backButtonCallback={onBackButtonClick}
      />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4`}>
        <ListingPageSubHeader heading={`Users List - ${accountId}`} />
        <ListingTableHeader
          heading={"Users"}
          searchBoxPlaceholder={"Search by User ID"}
          searchBoxValue={orgUsersQueryParams.search}
          searchBoxOnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          searchBoxOnClear={() => debouncedSetSearchKeyword("")}
        />
        <ModalEditListingColumns
          columns={columns}
          setColumns={setColumns}
          lsKey={"columns_ManagedUsers"}
        />
        <div className="py-4 mt-4 relative">
          <EditListingColumnsButton />
          <AdminTable
            viewportHeightOffset={"400"}
            columns={columns
              .filter((item) => item.show)
              .map((item) => item.name)}
            data={tableData}
            isLoading={isFetching}
            listingQueryParams={orgUsersQueryParams}
          />
          <Pagination
            pageSize={orgUsersQueryParams.page_size}
            handlePageSizeChange={(e: TSelectboxOption | null) => {
              dispatch(
                setListingQueryParams({
                  ...listingQueryParams,
                  users: {
                    ...orgUsersQueryParams,
                    page: 1,
                    page_size: parseInt(`${e?.value}`),
                  },
                })
              );
            }}
            totalPages={Math.ceil(count / orgUsersQueryParams.page_size) || 1}
            forcePage={orgUsersQueryParams.page - 1}
            handlePageClick={(data: TPaginationSelected) => {
              dispatch(
                setListingQueryParams({
                  ...listingQueryParams,
                  users: {
                    ...orgUsersQueryParams,
                    page: data?.selected + 1,
                  },
                })
              );
            }}
            paginCurrentPage={orgUsersQueryParams.page}
            paginItemsOnCurrentPage={results.length}
            paginTotalItemsCount={count}
          />
        </div>
      </div>
    </>
  );
};

export default ScreenManagedAccountUsers;
